import db from "../../../database";
import { message, Modal } from "antd";
import i18next from "i18next";
import NotFoundImg from "../../../assets/images/NotFoundImg.svg";
import { productPriceOnScan } from "../../../services/generic";
import { getEstimates, syncSingleProductByValue } from "../../../services";
import { v4 as uuidv4 } from "uuid";
import messages from "../../../errors/messages.en-IN.json";
import { stock } from "./stock";
import { getApplicablePrice } from "../../../utils/getPriceList";
import { shouldUseBatchPrice } from "../../../utils/resolvePricingPolicy";
import usePOSConfigStore from "../../../stores/usePOSConfigStore";
import useSessionStore from "../../../stores/useSessionStore";
import useAuthStore from "../../../stores/useAuthStore";
import useAdvanceOrderStore from "../../../stores/useAdvanceOrderStore";
import { generateDocumentNo } from "../../../utils/documentSequenceUtils";
import useTransactionStore from "../../../stores/useTransactionStore";
import { getPricingRules } from "../../../utils/pricingRulesCache";
import { trace } from "../../../utils/debugTrace";

// Indexed exact-match lookup on a single `products` field (value/upc/upcIndex
// are all indexed in both DexieAdapter and SQLiteAdapter schemas). Mirrors
// the pattern getProduct() already uses for the configured-barcode-format
// path: `.where(field).equalsIgnoreCase(value)` builds a plain, serializable
// query descriptor -- safe over IPC to the SQLite main process, unlike
// `.filter()`/`.and()` closures on a DB Collection. The `.filter()` below
// runs on the already-resolved plain array, not on the Collection itself.
const getIndexedProductMatches = async (field, value) => {
  if (!value) return [];
  return db.products
    .where(field)
    .equalsIgnoreCase(value)
    .toArray()
    .then((all) => all.filter((p) => p.isactive == true && p.productSegment !== "BG"));
};

const dedupeProducts = (products) => {
  const seen = new Set();
  return products.filter((p) => {
    const key = p.mProductId ?? p.id;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

export const barcodeScaner = async (
  data,
  tillData,
  tillLayout,
  addDefinedProduct,
  setBatchSetAvailable,
  setDisplayBatchSelection,
  setLayoutType,
  setIsProductsVisible,
  setProductsData,
  chackBarcodeFlag,
  setSelectedProductInCart,
  setProductSearchInput,
  productSearchInputRef,
  cart,
  authHeaders,
  setOptions,
  fetchOnlineBatches,
  setCart,
  setProductStock,
  setTempCart,
  setShowCompleteSaleStock,
  setCompleteSaleStockData,
  setShowPaymentMethods,
  history
) => {
  const t = i18next.t;
  try {
    // Early return if no data is provided
    if (!data) {
      console.error("No QR code data provided");
      return;
    }

    const isRFID = data.startsWith("RFID_"); // Example: Add a prefix check if RFID codes have a specific format
    if (isRFID) {
      return rfidScaner(
        data,
        tillData,
        tillLayout,
        addDefinedProduct,
        setBatchSetAvailable,
        setDisplayBatchSelection,
        setLayoutType,
        setIsProductsVisible,
        setProductsData,
        chackBarcodeFlag,
        setSelectedProductInCart,
        setProductSearchInput,
        productSearchInputRef,
        cart,
        authHeaders,
        setOptions,
        fetchOnlineBatches
      );
    }

    const shouldUseConfig = tillData?.posScanConfigs?.length > 0 && !chackBarcodeFlag;

    // Split data if a delimiter is configured
    let productsToProcess = [data]; // Default to single barcode/QR code
    if (shouldUseConfig) {
      const posConfig = tillData.posScanConfigs.filter((item) => item.formula !== null && item.scanTrxType === "PR");
      const posConfigItem = posConfig.filter((item) => data.startsWith(item.startsWith));
      const qrConfigItem = tillData.posScanConfigs.filter((item) => item.qrCode === "Y" && item.barcodeLength === data?.length);

      if (qrConfigItem?.length > 0) {
        const scannedCode = data;
        const bunitId = tillData?.tillAccess?.csBunit?.csBunitId;
        const response = await getEstimates({
          bunitId: bunitId,
          recordId: data,
          status: "PE",
          startRow: "0",
          endRow: "5",
          date: "",
          documentno: "",
        });
        if (response?.status === 200) {
          const data = JSON.parse(response?.data?.data?.getEstimates || "[]");
          if (data.estimates?.length > 0) {
            const record = data.estimates[0];
            const tillDocumentSequence = useSessionStore.getState().documentSequence;
            const cartData = JSON.parse(record.estimate_json || "{}");
            cartData.estimateId = record?.s_estimate_id;
            cartData.tillName = useAuthStore.getState().tillValue?.name;
            const isAdvanceOrder = record?.is_advanceorder === "Y";
            // BUG-2548: the estimate's own sOrderID can be one an earlier bill already
            // used; the server then answers 200 with no document number. Bill a plain
            // estimate under a new id (the bill links to the estimate by estimateId).
            if (!isAdvanceOrder) cartData.sOrderID = uuidv4().replace(/-/g, "").toUpperCase();
            // BUG-DUPLICATE-ESTIMATE-BILL: temporary diagnostic — logs every scan that
            // returns a still-"PE" estimate, so a rescan of the same estimateId within
            // seconds shows up as two "estimate found on scan" entries. Remove once confirmed.
            trace("EST-SCAN", "estimate found on scan", {
              estimateId: record?.s_estimate_id,
              scannedCode,
              status: record?.status,
              isAdvanceOrder,
              documentSequenceAtScan: tillDocumentSequence,
              preorderSequenceAtScan: useSessionStore.getState().preorderSequence?.nextNo,
            });
            if (isAdvanceOrder) {
              cartData.layAway = "Y";
              cartData.isAdvanceOrder = "Y";
              useAdvanceOrderStore.getState().setIsAdvanceOrderEnabled(true);
              const preSeq = useSessionStore.getState().preorderSequence;
              if (preSeq?.prefix) {
                cartData.documentno = generateDocumentNo(preSeq);
              } else {
                cartData.documentno = `${tillData.tillAccess.cwrTill.prefix}${tillDocumentSequence + 1}`;
              }
            } else {
              cartData.documentno = `${tillData.tillAccess.cwrTill.prefix}${tillDocumentSequence + 1}`;
            }

            let stockCheckResult = true;
            let isFinalValidation = false;
            // Same gap as useParkedBills.js's selectParkedBill: an estimate not
            // flagged is_advanceorder can still carry a stray "AD"-segment line.
            // Don't drop straight into payment for that case -- route it back
            // through the normal cart so handleCartTotalClick's advance gate applies.
            const hasBlockingAdvanceItem = !isAdvanceOrder && cartData?.items?.some((it) => it?.productSegment === "AD" && it?.weight > 0);
            if (!isAdvanceOrder && navigator.onLine && tillData?.tillAccess?.csBunit?.allowNegativeStock !== "Y") {
              stockCheckResult = await stock({
                cart: cartData,
                setCart,
                tillData,
                setProductStock,
                isFinalValidation,
                setTempCart,
                isCompleteSale: true,
                setShowCompleteSaleStock,
                setCompleteSaleStockData,
              });
              if (!stockCheckResult) {
                return;
              } else if (hasBlockingAdvanceItem) {
                message.warning(`${messages.errors.cart.advance_option_required}`);
              } else {
                setShowPaymentMethods(true);
              }
            } else if (hasBlockingAdvanceItem) {
              message.warning(`${messages.errors.cart.advance_option_required}`);
            } else {
              setShowPaymentMethods(true);
            }
            if (cartData?.customer?.isB2bEnabled === "Y") {
              localStorage.setItem("isB2bEnabled", "true");
              useTransactionStore.getState().setIsB2bEnabled("true");
            }
            if (cartData?.customer?.customerAddressIds) {
              localStorage.setItem("customerAddressIds", JSON.stringify(cartData.customer.customerAddressIds));
            }
            trace("EST-SCAN", "estimate cart staged", {
              estimateId: cartData.estimateId,
              previewDocumentNo: cartData.documentno,
              isRetrived: cartData.isRetrived,
            });
            setCart(cartData);
            const tempPosConfig = usePOSConfigStore.getState().posConfig;
            if (isAdvanceOrder && tempPosConfig?.enablePreOrder === "Y") {
              history.push("/preorder");
            }
            if (!isAdvanceOrder && tempPosConfig?.enablePreOrder === "Y") {
              history.push("/pos");
            }
            return;
          } else {
            trace("EST-SCAN", "estimate not found (already closed/invalid)", { scannedCode });
            message.warning(`The scanned estimate is no longer valid.`);
            return;
          }
        } else {
          message.warning("Something is wrong in get estimates service");
        }
      } else if (posConfigItem?.length > 0) {
        const config = posConfigItem[0];
        if (config.delimiter) {
          productsToProcess = data
            .split(config.delimiter)
            .filter(Boolean)
            .map((p) => p.trim());
        }
      } else {
      }
    } else {
    }

    for (const productData of productsToProcess) {
      if (shouldUseConfig) {
        await barcodeScanFromConfig(
          productData,
          tillData,
          tillLayout,
          addDefinedProduct,
          setBatchSetAvailable,
          setDisplayBatchSelection,
          setLayoutType,
          setIsProductsVisible,
          setProductsData,
          setSelectedProductInCart,
          setProductSearchInput,
          productSearchInputRef,
          authHeaders,
          setOptions,
          fetchOnlineBatches
        );
      } else {
        // Standard product search
        const posConfig = usePOSConfigStore.getState().posConfig;
        const normalizedSearch = productData?.toLowerCase().trim();
        const normalizedProductData = productData?.toString().trim();
        let products;

        const matchesNormalized = (p) => {
          const name = p.name?.toLowerCase().trim();
          const upcIndex = p.upcIndex?.toString().trim();
          const upc = p.upc?.toString().trim();
          const value = p.value?.toString().trim();
          return name?.includes(normalizedSearch) || upcIndex === normalizedProductData || upc === normalizedProductData || value === normalizedProductData;
        };

        // Fast path: try indexed exact-match lookups on value/upc/upcIndex
        // before scanning the full catalogue. A barcode almost always matches
        // one of these fields exactly, so this avoids reading every product
        // (and its UOM join) over IPC just to find one row. Only a name
        // substring search -- or an indexed miss -- needs the full scan below,
        // since `name` has no usable equality/prefix index for `.includes()`.
        if (posConfig?.is_smart_product_search === "Y") {
          // Smart search prioritizes an exact value/SKU match, but a UPC-only
          // barcode must still resolve via an indexed lookup -- not the full
          // catalogue scan -- when there's no value match (BUG: OFC scanning
          // still delayed for UPC-only barcodes under smart search).
          products = (await getIndexedProductMatches("value", normalizedProductData)).slice(0, 100);
          if (products.length === 0) {
            const [byUpc, byUpcIndex] = await Promise.all([
              getIndexedProductMatches("upc", normalizedProductData),
              getIndexedProductMatches("upcIndex", normalizedProductData),
            ]);
            products = dedupeProducts([...byUpc, ...byUpcIndex]).slice(0, 100);
          }
        } else {
          const [byValue, byUpc, byUpcIndex] = await Promise.all([
            getIndexedProductMatches("value", normalizedProductData),
            getIndexedProductMatches("upc", normalizedProductData),
            getIndexedProductMatches("upcIndex", normalizedProductData),
          ]);
          products = dedupeProducts([...byValue, ...byUpc, ...byUpcIndex]).slice(0, 100);
        }

        if (!products || products.length === 0) {
          // Use toArray() + JS filter — .filter() on DB collection breaks in SQLite
          // (closures like productData/search are lost when fn.toString() is sent over IPC)
          const allProds = await db.products.toArray();
          const activeProds = allProds.filter((p) => p.isactive == true && p.productSegment !== "BG");
          products = activeProds.filter(matchesNormalized).slice(0, 100);
        }

        // Handle product search results
        if (!products || products.length === 0) {
          // Cache miss — try on-demand single-product sync once, then re-run
          // the same local filter. Product may exist in backend but not yet
          // synced to local DB. Ported from roadmap bc3b9831 / ad6b7339.
          try {
            const tillId = tillData?.tillAccess?.cwrTill?.cwrTillID;
            const synced = await syncSingleProductByValue(tillData, uuidv4(), tillId, normalizedProductData);
            if (synced) {
              const allProdsAfter = await db.products.toArray();
              const activeAfter = allProdsAfter.filter((p) => p.isactive == true && p.productSegment !== "BG");
              products = activeAfter.filter(matchesNormalized).slice(0, 100);
            }
          } catch (err) {
            console.warn("[Scanner] single-product sync failed:", err?.message);
          }
          if (!products || products.length === 0) {
            await upcPricingRule(productData, t, addDefinedProduct, setProductSearchInput, productSearchInputRef, setOptions);
            continue;
          }
        }

        // Single product found
        if (products.length === 1) {
          const product = products[0];
          if (tillLayout === 3) {
            localStorage.setItem("layoutType", 1);
            setLayoutType(1);
            const customEvent = new CustomEvent("customStorageChange", {
              detail: { key: "layoutType", newValue: 1 },
            });
            window.dispatchEvent(customEvent);
          }

          // Handle batched products
          if (product.batchedProduct === "Y" && product.batchedForSale === "Y") {
            await handleBatchedProduct(product, productData, addDefinedProduct, setBatchSetAvailable, setDisplayBatchSelection, fetchOnlineBatches, cart);
          } else {
            const modifiedQty = product.weight >= 0 ? product.weight + 1 : 1;
            await addDefinedProduct(product, product.upc, null, null, product.sunitprice, true, modifiedQty, true, null, null, productData);
          }
        }
        // Multiple products found
        else if (products.length > 1) {
          if (posConfig?.product_search_display_mode !== "dropdown") {
            setIsProductsVisible(true);
          }
          setProductsData(products);
        }
      }
    }
  } catch (error) {
    console.error("Barcode scanning error:", error, "QR code:", data);
    await upcPricingRule(data, t, addDefinedProduct, setProductSearchInput, productSearchInputRef, setOptions);
  }
};

// Unchanged Supporting Functions (included for completeness)
const handleBatchedProduct = async (product, data, addDefinedProduct, setBatchSetAvailable, setDisplayBatchSelection, fetchOnlineBatches, cart) => {
  try {
    // No batch data cached locally for this batched product (catalog sync
    // gap) -- ask the backend live instead of silently treating it as
    // unbatched, which would otherwise queue a sale/return with no batch
    // reference and fail later at the backend's stock-movement trigger. Only
    // safe in Online-Only mode (disableOfflineBilling === "Y"), where
    // connectivity is guaranteed -- offline-capable tills fall back to the
    // original plain add instead of risking a network call.
    if (!product?.mBatch || !Array.isArray(product.mBatch) || product.mBatch.length === 0) {
      const posConfig = usePOSConfigStore.getState().posConfig;
      if (posConfig?.disableOfflineBilling === "Y") {
        const productDefined = createProductDefinition(product, null);
        setBatchSetAvailable([productDefined]);
        fetchOnlineBatches([productDefined]);
      } else {
        addDefinedProduct(product, product.upc, null, null, product.sunitprice);
      }
      return;
    }

    // Find batch by barcode
    const batchProductIndex = product.mBatch.findIndex((bp) => bp.batchno?.toLowerCase() === data.toLowerCase());

    // Single batch with exact match
    if (batchProductIndex >= 0 && product.multiPrice === "N") {
      const batch = product.mBatch[batchProductIndex];
      if (!batch?.batchno || !batch?.mBatchId || !batch?.price) {
        console.error("Invalid batch data", batch);
        return; // Exit if any required batch properties are missing
      }
      return await addDefinedProduct(product, batch.upc, batch.batchno, batch.mBatchId, batch.price, null, null, null, null, null, data);
    }

    // Single batch locally — use it directly instead of re-resolving via the
    // live stock lookup, matching pickProduct's behavior (products popup).
    // The live lookup can disagree with the locally-cached batch id, and
    // verifyStock then rejects it as out of stock even though the product
    // is actually available.
    if (product.mBatch.length === 1) {
      const batch = product.mBatch[0];
      if (batch?.batchno && batch?.mBatchId && batch?.price !== undefined) {
        return await addDefinedProduct(product, batch.upc, batch.batchno, batch.mBatchId, batch.price, null, null, null, null, null, data);
      }
    }

    // Multiple batches — open Select Batch drawer
    // const productSet = product.mBatch
    //   .map(async (batchItem) => {
    //     // Ensure batchItem and necessary properties are defined
    //     if (!batchItem?.batchno || !batchItem?.mBatchId || batchItem?.price === undefined) {
    //       console.error("Invalid batch item data", batchItem);
    //       return null; // Skip invalid batch item
    //     }

    //     const modifiedProduct = { ...product };

    //     // Tax calculation logic with valid data checks
    //     if (modifiedProduct?.overRideTax === "Y" && batchItem?.price <= modifiedProduct?.overRideCondition) {
    //       const originalPrice = batchItem.price - batchItem.price * (100 / (100 + modifiedProduct.taxRate));
    //       const taxedPrice = originalPrice + (originalPrice * modifiedProduct.contraRate) / 100;

    //       modifiedProduct.cTaxId = modifiedProduct.contraTaxId;
    //       modifiedProduct.taxRate = modifiedProduct.contraRate;
    //       batchItem.price = taxedPrice;
    //     }

    //     return createProductDefinition(modifiedProduct, batchItem);
    //   })
    //   .filter(Boolean); // Filter out null values

    const productSet = product.mBatch
      .map((batchItem) => {
        if (!batchItem?.batchno || !batchItem?.mBatchId || batchItem?.price === undefined) {
          console.error("Invalid batch item data", batchItem);
          return null;
        }

        const modifiedProduct = { ...product };

        if (modifiedProduct?.overRideTax === "Y" && batchItem.price <= modifiedProduct?.overRideCondition) {
          const originalPrice = batchItem.price - batchItem.price * (100 / (100 + modifiedProduct.taxRate));

          const taxedPrice = originalPrice + (originalPrice * modifiedProduct.contraRate) / 100;

          modifiedProduct.cTaxId = modifiedProduct.contraTaxId;
          modifiedProduct.taxRate = modifiedProduct.contraRate;
          batchItem.price = taxedPrice;
        }

        const productDefined = createProductDefinition(modifiedProduct, batchItem);
        const selectedPrice = getApplicablePrice({
          priceList: modifiedProduct?.priceList,
          selectedUomId: modifiedProduct?.uomData?.[0]?.csUomId,
          sPriceListId: cart?.customer?.sPriceListId,
          alternateUom: modifiedProduct?.alternateUom || [],
          primaryUomId: modifiedProduct?.uom || null,
        });
        const useBatch = shouldUseBatchPrice(modifiedProduct);
        if (useBatch) {
          productDefined.salePrice = batchItem.price;
          productDefined.realPrice = batchItem.price;
        } else if (selectedPrice?.pricestd != null) {
          productDefined.salePrice = selectedPrice.pricestd;
          productDefined.realPrice = selectedPrice.pricestd;
        }
        return productDefined;
      })
      .filter(Boolean);

    // Check if productSet has valid entries
    if (productSet.length > 0) {
      setBatchSetAvailable(productSet);
      fetchOnlineBatches(productSet);
    }
  } catch (error) {
    console.error("Batched product handling error:", error);
  }
};

const createProductDefinition = (product, batchItem) => ({
  batchno: batchItem?.batchno ?? "",
  description: product?.description ?? "",
  discount: 0,
  discountName: "",
  imageurl: product?.imageurl ?? "",
  isDecimal: product?.isDecimal ?? false,
  isManualQty: product?.isManualQty ?? false,
  restrictPosQtyEdit: product?.restrictPosQtyEdit || "N",
  showAlternateUomInPos: product?.showAlternateUomInPos || "N",
  posPriceEdit: product?.posPriceEdit || "N",
  interTaxId: product?.interTaxId || "",
  interTaxRate: product?.interTaxRate || 0,
  interTaxName: product?.interTaxName || "",
  alternateUom: product?.alternateUom || null,
  companion: product?.companion || "N",
  isPromoApplicable: product?.isPromoApplicable ?? false,
  isReturn: false,
  mBatchId: batchItem?.mBatchId ?? null,
  mPricingruleId: null,
  name: product?.name ?? "",
  nettotal: 0,
  primaryOrderLine: null,
  productId: product?.mProductId ?? "",
  realPrice: batchItem?.price ?? 0,
  listPrice: product?.slistprice ?? 0,
  sunitprice: product?.sunitprice ?? 0,
  originalPrice: product?.sunitprice ?? 0,
  productSegment: product.productSegment || "",
  returnQty: null,
  salePrice: batchItem?.price ?? 0,
  mrpPrice: batchItem?.listPrice ?? 0,
  stock: product?.onhandQty ?? 0,
  taxCategory: product?.taxCategory ?? "",
  taxName: product?.taxName ?? "N",
  taxFlag: product?.taxFlag ?? "N",
  tax: product?.cTaxId ?? 0,
  taxAmount: 0,
  taxRate: product?.taxRate ?? 0,
  uom: product?.csUomId ?? "",
  uom_name: product?.uomName || product?.uomData?.[0]?.name || "",
  isDecimalQty: product?.uomData?.[0]?.decimal === "Y" ?? false,
  isQtyDesimal: product?.uomData?.[0]?.stdprecision ?? 2,
  upc: batchItem?.upc ?? "",
  value: product?.value ?? 0,
  weight: 0,
  shortDescription: product?.shortDescription ?? "",
  hsncode: product?.hsncode ?? "",
  csBunitId: product?.csBunitId ?? "",
  mProductCategoryId: product?.mProductCategoryId ?? "",
  productManufacturerId: product?.productManufacturerId ?? "",
  productBrandId: product?.productBrandId ?? "",
  productBrandName: product?.productBrandName ?? "",
  batchedProduct: product?.batchedProduct ?? false,
  batchedForSale: product?.batchedForSale ?? false,
  batchedForStock: product?.batchedForStock ?? false,
  multiPrice: product?.multiPrice ?? "N",
  shelfLife: product?.shelfLife ?? "",
  addNewLine: product?.addNewLine || "N",
});

const getProduct = async (
  formula,
  newData,
  tillData,
  data,
  tillLayout,
  addDefinedProduct,
  setBatchSetAvailable,
  setDisplayBatchSelection,
  setLayoutType,
  setIsProductsVisible,
  setProductsData,
  setSelectedProductInCart,
  setProductSearchInput,
  productSearchInputRef,
  config,
  authHeaders,
  setOptions,
  fetchOnlineBatches
) => {
  let productValue =
    typeof newData === "string" && newData.includes("productcode:")
      ? newData.match(/productcode:([^,]+)/)?.[1]?.trim()
      : config.barcodeLength === newData.length
        ? newData.substr(Number(formula.property?.["product.value"]?.position ?? 0) - 1, Number(formula.property?.["product.value"]?.length ?? 0))
        : newData;

  let batchno = typeof newData === "string" && newData.includes("batchno:") ? newData.match(/batchno:([^,]+)/)?.[1]?.trim() : null;

  productValue = config?.delimiter ? productValue.replace(/^0+/, "") : productValue;

  const operators = ["+", "-", "/", "*"];
  const operatorFunctions = {
    "+": (a, b) => a + b,
    "-": (a, b) => a - b,
    "*": (a, b) => a * b,
    "/": (a, b) => a / b,
  };
  await db.products
    .where("value")
    .equalsIgnoreCase(productValue)
    .toArray()
    .then((allProducts) => allProducts.filter((p) => p.isactive == true && p.productSegment !== "BG"))
    .then(async (product) => {
      const uomData = product[0]?.uomData[0];
      let orderNo = "";
      const currenciesList = tillData.tillAccess.csBunit.currencies;
      let scanFlag = false;

      let cartlineUnitprice;
      let cartlineTotal;
      let cartlineQty;
      if (formula.property) {
        if (formula?.property["cartline.unitprice"] !== undefined) {
          if (typeof formula.property["cartline.unitprice"] === "number") {
            cartlineUnitprice = formula.property["cartline.unitprice"];
          } else if (typeof formula.property["cartline.unitprice"] === "string") {
            const foundOperator = operators.find((op) => formula.property["cartline.unitprice"].includes(op));
            const splitArr = formula.property["cartline.unitprice"].split(foundOperator);
            let unitpriceArr = splitArr.map((splitItem) => {
              let value;
              if (splitItem.includes("cartline")) {
                if (splitItem.includes("qty")) {
                  if (typeof formula.property["cartline.qty"] === "number") {
                    value = formula.property["cartline.qty"];
                  } else if (typeof formula.property["cartline.qty"] === "object") {
                    value = parseFloat(
                      (
                        Number(newData.substr(Number(formula.property["cartline.qty"]?.position - 1), Number(formula.property["cartline.qty"]?.length))) /
                        (uomData && uomData?.decimal === "Y" ? Math.pow(10, uomData?.stdprecision) : 1)
                      ).toFixed(uomData && uomData?.decimal === "Y" ? uomData?.stdprecision : 3)
                    );
                  }
                } else if (splitItem.includes("total")) {
                  if (typeof formula.property["cartline.total"] === "number") {
                    value = formula.property["cartline.total"];
                  } else if (typeof formula.property["cartline.total"] === "object") {
                    value = parseFloat(
                      (
                        Number(newData.substr(Number(formula.property["cartline.total"]?.position - 1), Number(formula.property["cartline.total"]?.length))) /
                        (currenciesList[0]?.stdPrecision ? Math.pow(10, currenciesList[0].stdPrecision) : 100)
                      ).toFixed(currenciesList[0].stdPrecision ? currenciesList[0].stdPrecision : 2)
                    );
                  }
                } else if (splitItem.includes("unitprice")) {
                  if (typeof formula.property["cartline.unitprice"] === "number") {
                    value = formula.property["cartline.unitprice"];
                  } else if (typeof formula.property["cartline.unitprice"] === "object") {
                    value = parseFloat(
                      (
                        Number(newData.substr(Number(formula.property["cartline.unitprice"]?.position - 1), Number(formula.property["cartline.unitprice"]?.length))) /
                        (currenciesList[0]?.stdPrecision ? Math.pow(10, currenciesList[0].stdPrecision) : 100)
                      ).toFixed(currenciesList[0].stdPrecision ? currenciesList[0].stdPrecision : 2)
                    );
                  }
                }
              } else {
                value = product[0].sunitprice;
              }
              return value;
            });
            cartlineUnitprice = operatorFunctions[foundOperator](unitpriceArr[0], unitpriceArr[1]);
          } else {
            cartlineUnitprice = parseFloat(
              (
                Number(newData.substr(Number(formula.property["cartline.unitprice"]?.position - 1), Number(formula.property["cartline.unitprice"]?.length))) /
                (currenciesList[0]?.stdPrecision ? Math.pow(10, currenciesList[0].stdPrecision) : 100)
              ).toFixed(currenciesList[0].stdPrecision ? currenciesList[0].stdPrecision : 2)
            );
          }
        }

        if (formula?.property["cartline.total"] !== undefined) {
          if (typeof formula.property["cartline.total"] === "number") {
            cartlineTotal = formula.property["cartline.total"];
          } else if (typeof formula.property["cartline.total"] === "string") {
            const foundOperator = operators.find((op) => formula.property["cartline.total"].includes(op));
            const splitArr = formula.property["cartline.total"].split(foundOperator);
            let totalArr = splitArr.map((splitItem) => {
              let value;
              if (splitItem.includes("cartline")) {
                if (splitItem.includes("qty")) {
                  if (typeof formula.property["cartline.qty"] === "number") {
                    value = formula.property["cartline.qty"];
                  } else if (typeof formula.property["cartline.qty"] === "object") {
                    value = parseFloat(
                      (
                        Number(newData.substr(Number(formula.property["cartline.qty"]?.position - 1), Number(formula.property["cartline.qty"]?.length))) /
                        (uomData && uomData?.decimal === "Y" ? Math.pow(10, uomData?.stdprecision) : 1)
                      ).toFixed(uomData && uomData?.decimal === "Y" ? uomData?.stdprecision : 3)
                    );
                  }
                } else if (splitItem.includes("total")) {
                  if (typeof formula.property["cartline.total"] === "number") {
                    value = formula.property["cartline.total"];
                  } else if (typeof formula.property["cartline.total"] === "object") {
                    value = parseFloat(
                      (
                        Number(newData.substr(Number(formula.property["cartline.total"]?.position - 1), Number(formula.property["cartline.total"]?.length))) /
                        (currenciesList[0]?.stdPrecision ? Math.pow(10, currenciesList[0].stdPrecision) : 100)
                      ).toFixed(currenciesList[0].stdPrecision ? currenciesList[0].stdPrecision : 2)
                    );
                  }
                } else if (splitItem.includes("unitprice")) {
                  if (typeof formula.property["cartline.unitprice"] === "number") {
                    value = formula.property["cartline.unitprice"];
                  } else if (typeof formula.property["cartline.unitprice"] === "object") {
                    value = parseFloat(
                      (
                        Number(newData.substr(Number(formula.property["cartline.unitprice"]?.position - 1), Number(formula.property["cartline.unitprice"]?.length))) /
                        (currenciesList[0]?.stdPrecision ? Math.pow(10, currenciesList[0].stdPrecision) : 100)
                      ).toFixed(currenciesList[0].stdPrecision ? currenciesList[0].stdPrecision : 2)
                    );
                  }
                }
              } else {
                value = product[0]?.sunitprice || 0;
              }
              return value;
            });
            cartlineTotal = operatorFunctions[foundOperator](totalArr[0], totalArr[1]);
          } else {
            const propertyTotalprice = formula.property["cartline.total"];
            const totalPrice = newData.substr(Number(propertyTotalprice.position - 1), Number(propertyTotalprice.length));
            const stdPrecision = currenciesList[0]?.stdPrecision || 2;

            if (propertyTotalprice.decimalPlaces !== undefined) {
              // Barcode's own encoded decimal-digit count, stated explicitly in the scan-config
              // formula. Distinct from the tenant's currency stdPrecision — see BUG-1684.
              // checkDigit: true drops the field's trailing character first — verified against
              // real OFC labels, the last digit of a 7-length cartline.total field is the
              // barcode's standard EAN-13 check digit, not a price digit (BUG-2036).
              const priceDigits = propertyTotalprice.checkDigit ? totalPrice.slice(0, -1) : totalPrice;
              const decimalDigits = Number(propertyTotalprice.decimalPlaces);
              cartlineTotal = parseFloat(
                parseFloat(priceDigits.slice(0, -decimalDigits) + "." + priceDigits.slice(-decimalDigits)).toFixed(stdPrecision)
              );
            } else {
              cartlineTotal = parseFloat((Number(totalPrice) / Math.pow(10, stdPrecision)).toFixed(stdPrecision));
            }
          }
        }

        if (formula?.property["cartline.qty"] !== undefined) {
          if (typeof formula.property["cartline.qty"] === "number") {
            cartlineQty = formula.property["cartline.qty"];
          } else if (typeof formula.property["cartline.qty"] === "string") {
            const foundOperator = operators.find((op) => formula.property["cartline.qty"].includes(op));
            const splitArr = formula.property["cartline.qty"].split(foundOperator);
            let qtyArr = splitArr.map((splitItem) => {
              let value;
              if (splitItem.includes("cartline")) {
                if (splitItem.includes("qty")) {
                  if (typeof formula.property["cartline.qty"] === "number") {
                    value = formula.property["cartline.qty"];
                  } else if (typeof formula.property["cartline.qty"] === "object") {
                    value = parseFloat(
                      (
                        Number(newData.substr(Number(formula.property["cartline.qty"]?.position - 1), Number(formula.property["cartline.qty"]?.length))) /
                        (uomData && uomData?.decimal === "Y" ? Math.pow(10, uomData?.stdprecision) : 1)
                      ).toFixed(uomData && uomData?.decimal === "Y" ? uomData?.stdprecision : 3)
                    );
                  }
                } else if (splitItem.includes("total")) {
                  if (typeof formula.property["cartline.total"] === "number") {
                    value = formula.property["cartline.total"];
                  } else if (typeof formula.property["cartline.total"] === "object") {
                    value = parseFloat(
                      (
                        Number(newData.substr(Number(formula.property["cartline.total"]?.position - 1), Number(formula.property["cartline.total"]?.length))) /
                        (currenciesList[0]?.stdPrecision ? Math.pow(10, currenciesList[0].stdPrecision) : 100)
                      ).toFixed(currenciesList[0].stdPrecision ? currenciesList[0].stdPrecision : 2)
                    );
                  }
                } else if (splitItem.includes("unitprice")) {
                  if (typeof formula.property["cartline.unitprice"] === "number") {
                    value = formula.property["cartline.unitprice"];
                  } else if (typeof formula.property["cartline.unitprice"] === "object") {
                    value = parseFloat(
                      (
                        Number(newData.substr(Number(formula.property["cartline.unitprice"]?.position - 1), Number(formula.property["cartline.unitprice"]?.length))) /
                        (currenciesList[0]?.stdPrecision ? Math.pow(10, currenciesList[0].stdPrecision) : 100)
                      ).toFixed(currenciesList[0].stdPrecision ? currenciesList[0].stdPrecision : 2)
                    );
                  }
                }
              } else if (splitItem.includes("product")) {
                if (splitItem.includes("unitprice")) {
                  value = product[0]?.sunitprice;
                } else if (splitItem.includes("nettotal")) {
                  value = cartlineTotal;
                }
              } else {
                value = product[0].sunitprice;
              }
              return value;
            });
            const flag = process.env.REACT_APP_OFC_SCAN_MODE == "true" ? true : false;
            if (flag) {
              cartlineQty = cartlineTotal / product[0].sunitprice >= 0 ? cartlineTotal / product[0].sunitprice : 1;
              let multipliedValue = cartlineQty * product[0].sunitprice * 100;
              let roundedValue = Math.floor(multipliedValue);
              cartlineTotal =
                Number(Number(roundedValue / 100).toFixed(product[0].uomData[0]?.stdprecision > 0 ? product[0].uomData[0]?.stdprecision : 2)) >= 0
                  ? Number(Number(roundedValue / 100).toFixed(product[0].uomData[0]?.stdprecision > 0 ? product[0].uomData[0]?.stdprecision : 2))
                  : product[0].sunitprice;
              cartlineQty = cartlineQty?.toFixed(uomData && uomData?.decimal === "Y" ? uomData?.stdprecision : 3);
            } else {
              cartlineQty = parseFloat(
                operatorFunctions[foundOperator](qtyArr[0], qtyArr[1]).toFixed(uomData && uomData?.decimal === "Y" ? uomData?.stdprecision : 3)
              );
            }
          } else if (typeof formula.property["cartline.qty"] === "object") {
            cartlineQty = parseFloat(
              (
                Number(newData.substr(Number(formula.property["cartline.qty"]?.position - 1), Number(formula.property["cartline.qty"]?.length))) /
                (uomData && uomData?.decimal === "Y" ? Math.pow(10, uomData?.stdprecision) : 1)
              ).toFixed(uomData && uomData?.decimal === "Y" ? uomData?.stdprecision : 3)
            );
          }
        }

        if (formula?.property["production.orderNo"] !== undefined) {
          orderNo = newData.substr(Number(formula.property["production.orderNo"]?.position - 1), Number(formula.property["production.orderNo"]?.length));
        }

        if (formula?.property["product.unitprice"] !== undefined) {
          if (formula.property["product.unitprice"]?.type === "event") {
            const workflowRules = await db.POSWorkFlowRules.toArray();
            // const approvers = await db.approvers.toArray();

            if (workflowRules && workflowRules.length > 0) {
              await Promise.all(
                workflowRules.map(async (res) => {
                  if (res.eventId === "productPriceOnScan") {
                    const serverUrl = process.env.REACT_APP_serverUrl;
                    const CWCoreServicesUrl = process.env.REACT_APP_genericUrl;

                    if (res.apiSource === "APB") {
                      const value = { orderno: orderNo, value: productValue };
                      const newStringifiedFields = JSON.stringify(value).replace(/\\"/g, '\\"');
                      const newStringRequest = JSON.stringify(newStringifiedFields);
                      const apiBuilder = {
                        query: `mutation ExecuteAPIBuilder($apiBuilderId: String, $params: String) { executeAPIBuilder(apiBuilderId: $apiBuilderId, params: $params)}`,
                        variables: { apiBuilderId: res.apiEndPoint, params: newStringifiedFields },
                      };
                      const headers = {
                        "Content-Type": "application/json",
                        Authorization: `${authHeaders?.access_token}`,
                      };

                      const apiBuilderResponse = await productPriceOnScan(apiBuilder);

                      if (apiBuilderResponse.data.data.executeAPIBuilder.length > 0) {
                        const response = JSON.parse(apiBuilderResponse.data.data.executeAPIBuilder);
                        product[0].mBatchId = response?.length > 0 ? response[0]?.m_batch_id : null;
                        product[0].batchno = response?.length > 0 ? response[0]?.batch_no : null;
                        cartlineUnitprice = parseFloat(response?.length > 0 ? response[0].sale_price.toFixed(currenciesList[0].stdPrecision || 2) : 0);
                      }
                    }
                  }
                })
              );
            }
          }
        }
      }
      if (cartlineUnitprice || cartlineTotal) {
        scanFlag = true;
      }

      if (product.length <= 0) {
        let chackBarcodeFlag = true;
        await barcodeScaner(
          data,
          tillData,
          tillLayout,
          addDefinedProduct,
          setBatchSetAvailable,
          setDisplayBatchSelection,
          setLayoutType,
          setIsProductsVisible,
          setProductsData,
          chackBarcodeFlag,
          setSelectedProductInCart,
          setProductSearchInput,
          productSearchInputRef,
          null,
          null,
          setOptions,
          fetchOnlineBatches
        );
      } else {
        product[0].taxFlag = formula?.calculateTax || "N";
        if (product[0].taxFlag === "Y") {
          cartlineUnitprice = cartlineUnitprice ? cartlineUnitprice : product[0]?.sunitprice;
          let taxValue = (cartlineUnitprice * (product[0].taxRate / 100)) / (1 + product[0].taxRate / 100);
          cartlineUnitprice = parseFloat((cartlineUnitprice - taxValue).toFixed(currenciesList[0].stdPrecision ? currenciesList[0].stdPrecision : 2));
          product[0].sunitprice = cartlineUnitprice;
        }
        if (batchno) {
          product[0].batchno = batchno;
        }

        await addDefinedProduct(
          product[0],
          product[0].upc,
          product[0]?.batchno || null,
          product[0]?.mBatchId || null,
          cartlineUnitprice || product[0].sunitprice,
          true,
          cartlineQty || 1,
          false,
          cartlineTotal,
          scanFlag,
          data
        );
      }
    });
};

const barcodeScanFromConfig = async (
  data,
  tillData,
  tillLayout,
  addDefinedProduct,
  setBatchSetAvailable,
  setDisplayBatchSelection,
  setLayoutType,
  setIsProductsVisible,
  setProductsData,
  setSelectedProductInCart,
  setProductSearchInput,
  productSearchInputRef,
  authHeaders,
  setOptions,
  fetchOnlineBatches
) => {
  // Helper function to process a product with a config
  const processSingleProduct = async (config, productData) => {
    let formula = JSON.parse(config.formula);
    formula.calculateTax = config.calculateTax || "N";
    await getProduct(
      formula,
      productData,
      tillData,
      productData,
      tillLayout,
      addDefinedProduct,
      setBatchSetAvailable,
      setDisplayBatchSelection,
      setLayoutType,
      setIsProductsVisible,
      setProductsData,
      setSelectedProductInCart,
      setProductSearchInput,
      productSearchInputRef,
      config,
      authHeaders,
      setOptions,
      fetchOnlineBatches
    );
  };

  let newData = data.toString();
  let posConfig = tillData.posScanConfigs.filter((item) => item.formula !== null && item.scanTrxType === "PR");
  let productsToProcess = [];

  // Determine products to process based on config
  // if (posConfig?.length === 1) {
  //   let posConfigItem = posConfig?.filter((item) => newData.startsWith(item.startsWith));
  //   productsToProcess.push({ config: posConfigItem[0], data: newData });
  // } else {
  let posConfigItem = posConfig?.filter((item) => newData.startsWith(item.startsWith));
  if (posConfigItem.length > 0) {
    let config = posConfigItem[0];
    productsToProcess.push({ config: config, data: newData });
  } else if (newData.startsWith("{")) {
    let config = posConfig?.filter((item) => item.formula !== null && item.scanTrxType === "PR" && item?.qrCode === "Y")[0];
    productsToProcess.push({ config: config, data: newData });
  }
  // }

  // Process all collected products using `for...of`
  if (productsToProcess.length > 0) {
    for (const { config, data } of productsToProcess) {
      await processSingleProduct(config, data);
    }
  } else {
    let chackBarcodeFlag = true;
    await barcodeScaner(
      data,
      tillData,
      tillLayout,
      addDefinedProduct,
      setBatchSetAvailable,
      setDisplayBatchSelection,
      setLayoutType,
      setIsProductsVisible,
      setProductsData,
      chackBarcodeFlag,
      setSelectedProductInCart,
      setProductSearchInput,
      productSearchInputRef,
      productSearchInputRef,
      authHeaders,
      setOptions,
      fetchOnlineBatches
    );
  }
};

const rfidScaner = (
  data,
  tillData,
  tillLayout,
  addDefinedProduct,
  setBatchSetAvailable,
  setDisplayBatchSelection,
  setLayoutType,
  setIsProductsVisible,
  setProductsData,
  chackBarcodeFlag,
  setSelectedProductInCart,
  setProductSearchInput,
  productSearchInputRef,
  cart,
  authHeaders,
  setOptions,
  fetchOnlineBatches
) => {
  db.rfidData
    .where("tag_value")
    .equalsIgnoreCase(data)
    .toArray()
    .then(async (item) => {
      if (item.length > 0) {
        const scannedProductCode = item[0].product_code;
        const existingIndex = cart?.items?.findIndex((item) => item.value === scannedProductCode);
        if (existingIndex === -1) {
          await barcodeScaner(
            item[0].product_code,
            tillData,
            tillLayout,
            addDefinedProduct,
            setBatchSetAvailable,
            setDisplayBatchSelection,
            setLayoutType,
            setIsProductsVisible,
            setProductsData,
            chackBarcodeFlag,
            setSelectedProductInCart,
            setProductSearchInput,
            productSearchInputRef,
            cart,
            authHeaders,
            setOptions,
            fetchOnlineBatches
          );
          db.rfidData.update(item[0].tag_value, { tag_status: "SO" });
          if (tillLayout === 3) {
            localStorage.setItem("layoutType", 1);
            setLayoutType(1);
            const customEvent = new CustomEvent("customStorageChange", {
              detail: { key: "layoutType", newValue: 1 },
            });
            window.dispatchEvent(customEvent);
          }
        } else {
          message.info(`${messages.info.already_scanned}`);
        }
      }
    });
};

const upcPricingRule = async (data, t, addDefinedProduct, setProductSearchInput, productSearchInputRef, setOptions) => {
  const pr = getPricingRules();
  let productFlag = false;

  for (const ele of pr) {
    if (ele.upc === data) {
      const allPro = await db.products
        .where("mProductId")
        .equalsIgnoreCase(ele.mPricingXProducts[0].mProductId)
        .toArray();
      let pro = allPro.filter((product) => product.isactive == true && product.productSegment !== "BG");

      // Out-of-stock pricing-rule product absent from local DB → fetch on-demand,
      // then re-read. Guarded by length === 0 so the in-stock path is unchanged.
      if (pro.length === 0 && ele.mPricingXProducts[0]?.mProductId) {
        try {
          const tillData = useAuthStore.getState().tillData;
          const syncId = uuidv4().replace(/-/g, "").toUpperCase();
          const synced = await syncSingleProductByValue(tillData, syncId, tillData?.tillAccess?.cwrTill?.cwrTillID, ele.mPricingXProducts[0].mProductId.toString().trim());
          if (synced) {
            const refetched = await db.products.where("mProductId").equalsIgnoreCase(ele.mPricingXProducts[0].mProductId).toArray();
            pro = refetched.filter((product) => product.isactive == true && product.productSegment !== "BG");
          }
        } catch (err) {
          console.error("Scan pricing-rule on-demand sync failed:", err);
        }
      }

      if (pro.length > 0) {
        pro[0].upcPricingRule = ele.upc;
        productFlag = true;
        await addDefinedProduct(pro[0], pro[0].upc, null, null, pro[0].sunitprice, true, ele.mPricingXProducts[0].quantity, null, null, null, data);
      }
    }
  }

  if (!productFlag) {
    const posConfig = usePOSConfigStore.getState().posConfig;
    if (posConfig?.errorPopupAutoClose === "Y") {
      Modal.info({
        icon: null,
        okButtonProps: { style: { display: "none" } },
        okText: "Go Back",
        okButtonProps: { style: { display: "inline-block" } },
        content: (
          <div
            style={{
              fontSize: "18px",
              fontWeight: "500",
              textAlign: "center",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              paddingLeft: 0,
              fontSize: "1vw",
            }}
          >
            <img src={NotFoundImg} alt="Not Found" style={{ width: "120px", height: "100px", marginBottom: "5px", marginTop: "10px" }} />
            <p style={{ margin: "5px 0", fontSize: "2em", fontWeight: "600", color: "#0F0718" }}>Product Not Found</p>
            <hr style={{ width: "70%", color: "rgba(146,144,152,0.5" }} />
            <p style={{ color: "#0F0718", fontWeight: 400, fontSize: "1.25em", padding: "15px 15px 0 15px" }}>
              The scanned QR code ({data}) is not in the system. Please check the QR code or enter the item manually.
            </p>
          </div>
        ),
        style: {
          textAlign: "center",
          height: "50vh",
          width: "50%",
        },
        bodyStyle: {
          padding: "0",
        },
        closable: false,
        onOk: null,
      });

      setTimeout(() => {
        Modal.destroyAll();
      }, 1500);
    } else {
      Modal.info({
        icon: null,
        okText: "Ok",
        okButtonProps: { style: { display: "inline-block" } },
        content: (
          <div
            style={{
              fontSize: "18px",
              fontWeight: "500",
              textAlign: "center",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              paddingLeft: 0,
              fontSize: "1vw",
            }}
          >
            <img src={NotFoundImg} alt="Not Found" style={{ width: "120px", height: "100px" }} />
            <p style={{ margin: "5px 0", fontSize: "1.5em", fontWeight: "600", color: "#0F0718" }}>{data}</p>
            <p style={{ margin: "5px 0", fontSize: "2em", fontWeight: "600", color: "#0F0718" }}>Product Not Found</p>
            <hr style={{ width: "70%", color: "rgba(146,144,152,0.5" }} />
            <p style={{ color: "#0F0718", fontWeight: 400, fontSize: "1.25em", padding: "15px 15px 0 15px" }}>
              The scanned product is not in the system. Please check the barcode or enter the item manually.
            </p>
          </div>
        ),
        style: {
          textAlign: "center",
          height: "50vh",
          width: "50%",
        },
        closable: false,
      });
    }
  }
  setProductSearchInput("");
  setOptions([]);
  if (productSearchInputRef?.current) {
    productSearchInputRef?.current?.clearInput();
  }
};
